import { useState, useEffect } from "react";
import { apiClient } from "../../utils/api";

// ==============================================
// 🔧 테스트 모드 설정
// ==============================================
// 실제 서버로 전환하려면:
// 1. 아래 TEST_MODE를 false로 변경
// 2. apiClient import 주석 해제
// 3. 실제 API 호출 코드 주석 해제
// 4. Mock 데이터 코드를 주석 처리
// ==============================================
const TEST_MODE = false;

interface AgentSchema {
  [key: string]: any;
}

interface UseAgentDetailParams {
  agentId?: string;
}

interface UseAgentDetailResponse {
  schema: AgentSchema | null;
  isLoading: boolean;
  error: string;
}

// Mock 데이터
const mockAgentData = {
  data: {
    agent_id: "liberal_arts_career_agent",
    agent_image: "https://d33mx7rzqk8sgu.cloudfront.net/aivycommon/3bb15a7d-9641-4c34-85ac-b43114557297.jpeg",
    agent_title: "문과생 맞춤 진로 탐색 테스트",
    agent_kind: "text-modal",
    agent_description: "문과 성향 학생들의 강점과 관심사를 파악하여 한국의 다양한 전문직업을 추천하는 진로 탐색 테스트입니다.",
    agent_schema: {
      type: "object",
      properties: {
        communication_strength: {
          type: "string",
          description: "친구들과 이야기할 때 뭘 제일 잘하나요?",
          input_kind: "select",
          options: [
            "편지나 일기 쓰기",
            "큰 소리로 발표하기",
            "친구 마음 이해해주기",
            "차근차근 설명해주기",
            "재미있는 아이디어 내기"
          ]
        },
        work_environment: {
          type: "string",
          description: "어떤 곳에서 일하고 싶나요?",
          input_kind: "select",
          options: [
            "조용한 도서관 같은 곳",
            "법원이나 회의실",
            "TV방송국이나 신문사",
            "학교나 유치원",
            "미술관이나 박물관",
            "시청이나 구청",
            "큰 회사 사무실",
            "책을 만드는 출판사"
          ]
        },
        problem_solving_style: {
          type: "string",
          description: "어려운 문제가 생기면 어떻게 해결하나요?",
          input_kind: "select",
          options: [
            "규칙이나 법을 찾아본다",
            "여러 친구들 의견을 들어본다",
            "새로운 방법을 생각해본다",
            "예전에 어떻게 했는지 찾아본다",
            "계획을 세워서 차근차근 한다",
            "선생님이나 어른에게 물어본다"
          ]
        },
        interest_field: {
          type: "string",
          description: "어떤 것에 가장 관심이 많나요?",
          input_kind: "select",
          options: [
            "법과 정의, 옳고 그름",
            "책 읽기, 글쓰기",
            "역사, 옛날 이야기",
            "친구들 가르치기",
            "사람 마음 이해하기",
            "돈과 장사, 회사 일",
            "그림, 음악, 만들기",
            "외국어, 다른 나라",
            "뉴스, TV, 인터넷"
          ]
        },
        leadership_style: {
          type: "string",
          description: "친구들과 함께 놀 때 어떤 리더가 되나요?",
          input_kind: "select",
          options: [
            "규칙을 정하고 지키게 한다",
            "모든 친구 의견을 들어본다",
            "재미있는 놀이를 제안한다",
            "계획을 세워서 진행한다",
            "각 친구의 좋은 점을 찾아준다",
            "먼저 해보고 따라하게 한다"
          ]
        },
        work_value: {
          type: "string",
          description: "나중에 어른이 되어서 일할 때 뭐가 가장 중요할까요?",
          input_kind: "select",
          options: [
            "나쁜 사람을 혼내고 좋은 세상 만들기",
            "내가 하고 싶은 일 하며 성장하기",
            "예쁜 것, 멋진 것 만들기",
            "다른 사람들 가르치고 도와주기",
            "돈 많이 벌고 유명해지기",
            "외국에 가서 일하기",
            "새로운 것을 만들고 바꾸기",
            "내가 잘하는 것으로 인정받기"
          ]
        },
        skill_strength: {
          type: "string",
          description: "나는 뭘 제일 잘하나요?",
          input_kind: "select",
          options: [
            "생각을 잘 정리하고 따져보기",
            "말하기와 글쓰기",
            "새로운 것 생각해내기",
            "친구들 이끌고 정리하기",
            "친구 마음 이해하고 대화하기",
            "계획 세우고 아이디어 내기",
            "꼼꼼하게 정확히 하기",
            "친구들 설득하고 약속하기"
          ]
        },
        daily_activity: {
          type: "string",
          description: "하루 중 언제 가장 기분이 좋나요?",
          input_kind: "select",
          options: [
            "책을 읽고 새로운 걸 알 때",
            "글을 쓰고 그림을 그릴 때",
            "친구들과 재미있게 이야기할 때",
            "새로운 놀이를 생각해낼 때",
            "친구를 도와줄 때",
            "앞에서 발표하거나 의견 말할 때",
            "문제를 해결하고 잘 해냈을 때",
            "박물관이나 공연을 볼 때"
          ]
        },
        career_aspiration: {
          type: "string",
          description: "커서 어떤 멋진 일을 하고 싶나요?",
          input_kind: "select",
          options: [
            "나쁜 일을 없애고 세상을 좋게 만들기",
            "내가 만든 특별한 작품 자랑하기",
            "많은 사람들에게 영향 주기",
            "내가 좋아하는 분야에서 최고 되기",
            "세계 여러 나라에서 일하기",
            "어린 친구들 가르치고 키우기",
            "새로운 것을 만들어서 세상 바꾸기",
            "예술과 문화를 더 발전시키기"
          ]
        },
        additional_interests: {
          type: "string",
          description: "다른 재미있는 것이나 하고 싶은 일이 있으면 써주세요! (안 써도 괜찮아요)",
          input_kind: "text"
        }
      },
      required: [
        "communication_strength",
        "work_environment",
        "problem_solving_style",
        "interest_field",
        "leadership_style",
        "work_value",
        "skill_strength",
        "daily_activity",
        "career_aspiration"
      ]
    },
    child_license: true
  },
  relations: [], // 관련 에이전트 목록 (비어있음)
  status: true
};

const useAgentDetail = (params: UseAgentDetailParams): UseAgentDetailResponse => {
  const { agentId } = params;
  
  const [schema, setSchema] = useState<AgentSchema | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!agentId) {
      setError("Agent ID가 필요합니다.");
      return;
    }

    // Mock API 호출 시뮬레이션
    const fetchAgentSchema = async () => {
      setIsLoading(true);
      setError("");

      try {
        if (TEST_MODE) {
          // === Mock 데이터 사용 (테스트용) ===
          // 실제 API 호출 대신 setTimeout으로 로딩 시뮬레이션
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // agentId가 "liberal_arts_career_agent"인 경우에만 데이터 반환
          if (agentId === "liberal_arts_career_agent") {
            setSchema(mockAgentData);
          } else {
            // 다른 agentId인 경우 에러 처리
            setError("해당 에이전트를 찾을 수 없습니다.");
          }
        } else {
          // === 실제 API 호출 ===
          const response = await apiClient.get("/api/v1/agent/schema", {
            params: {
              agent_x_numb: agentId
            }
          });
          
          if (response.data) {
            setSchema(response.data);
          } else {
            setError("에이전트 스키마를 찾을 수 없습니다.");
          }
          
          // 실제 API 호출 코드가 주석처리되어 있음
          // throw new Error("실제 API 호출 코드의 주석을 해제해주세요.");
        }
      } catch (err) {
        console.error("에이전트 스키마 조회 오류:", err);
        setError("네트워크 오류가 발생했습니다. 다시 시도해주세요.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgentSchema();
  }, [agentId]);

  return {
    schema,
    isLoading,
    error,
  };
};

export default useAgentDetail;
export type { AgentSchema, UseAgentDetailParams, UseAgentDetailResponse };